import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Download, TrendingDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { analyticsApi, type AnalyticsPeriod } from "@/features/analytics/api";
import { cn } from "@/lib/utils";

const PERIODS: { value: AnalyticsPeriod; label: string }[] = [
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
];

function fmtPct(val: number | null | undefined) {
  if (val == null) return "";
  const pct = Math.round(val * 100);
  return pct >= 0 ? `+${pct}%` : `${pct}%`;
}

function shortLabel(iso: string) {
  if (!iso) return "";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function ChangeChip({ value }: { value: number | null | undefined }) {
  if (value == null) return null;
  const positive = value >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <div
      className={cn(
        "mt-1 inline-flex items-center gap-1 text-xs",
        positive
          ? "text-emerald-600 dark:text-emerald-400"
          : "text-rose-600 dark:text-rose-400",
      )}
    >
      <Icon className="size-3" />
      <span>
        {fmtPct(value)} <span className="text-muted-foreground">vs prev</span>
      </span>
    </div>
  );
}

function StatCard({
  label,
  value,
  change,
}: {
  label: string;
  value: string | number;
  change?: number | null;
}) {
  return (
    <Card>
      <CardContent className="space-y-1 p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-2xl font-semibold tabular-nums">{value}</p>
        <ChangeChip value={change} />
      </CardContent>
    </Card>
  );
}

export default function AnalyticsPage() {
  const [period, setPeriod] = useState<AnalyticsPeriod>("30d");
  const [channel, setChannel] = useState<"all" | "whatsapp" | "phone">("all");
  const channelParam = channel === "all" ? undefined : channel;

  const overviewQ = useQuery({
    queryKey: ["analytics", "overview", "all", period, channel],
    queryFn: () => analyticsApi.overview({ period, channel: channelParam }),
    staleTime: 30_000,
    retry: false,
  });

  const callsQ = useQuery({
    queryKey: ["analytics", "calls", "all", period, channel],
    queryFn: () =>
      analyticsApi.calls({ period, group_by: "day", channel: channelParam }),
    staleTime: 30_000,
    retry: false,
  });

  const latencyQ = useQuery({
    queryKey: ["analytics", "latency", "all", period, channel],
    queryFn: () => analyticsApi.latency({ period, channel: channelParam }),
    staleTime: 30_000,
    retry: false,
  });

  const exportMut = useMutation({
    mutationFn: (fmt: "csv" | "pdf") =>
      analyticsApi
        .exportReport({
          format: fmt,
          report_type: "calls",
          period: {},
          filters: {
            period,
            ...(channelParam ? { channel: channelParam } : {}),
          },
        })
        .then((blob) => ({ blob, fmt })),
    onSuccess: ({ blob, fmt }) => {
      const type = fmt === "pdf" ? "application/pdf" : "text/csv";
      const url = URL.createObjectURL(new Blob([blob], { type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "pdf" ? "call_report.pdf" : "calls_export.csv";
      a.click();
      URL.revokeObjectURL(url);
    },
    onError: () => toast.error("Export failed"),
  });

  const overview = overviewQ.data;
  const calls = overview?.calls;
  const sentiment = overview?.sentiment;
  const agents = overview?.agents;
  const topIntents = overview?.top_intents ?? [];
  const funnel = overview?.conversion_funnel ?? [];
  const costs = overview?.costs;

  const chartData = useMemo(
    () =>
      (callsQ.data?.data ?? []).map((d) => ({
        ...d,
        label: shortLabel(d.period),
        sentiment_pct:
          d.sentiment_score == null ? null : Math.round(d.sentiment_score * 100),
      })),
    [callsQ.data],
  );

  const latency = latencyQ.data;
  const latencyTrend = useMemo(
    () =>
      (latency?.trend ?? []).map((d) => ({
        ...d,
        label: shortLabel(d.period),
      })),
    [latency],
  );
  const hasLatency = (latency?.summary.sample_count ?? 0) > 0;
  const hasLatencySplit =
    (latency?.summary.avg_llm_ms ?? 0) > 0 ||
    (latency?.summary.avg_tts_ms ?? 0) > 0;
  const fmtMs = (ms: number | undefined) => {
    if (ms == null) return "—";
    return ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${ms}ms`;
  };

  const loading = overviewQ.isLoading || callsQ.isLoading;

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Insights into your voice operations.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={channel}
            onValueChange={(v) => setChannel(v as "all" | "whatsapp" | "phone")}
          >
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All channels</SelectItem>
              <SelectItem value="phone">Phone</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={period}
            onValueChange={(v) => setPeriod(v as AnalyticsPeriod)}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => exportMut.mutate("pdf")}
            disabled={exportMut.isPending}
          >
            <Download className="size-4" />
            {exportMut.isPending ? "Exporting…" : "PDF report"}
          </Button>
          <Button
            variant="outline"
            onClick={() => exportMut.mutate("csv")}
            disabled={exportMut.isPending}
          >
            <Download className="size-4" />
            CSV
          </Button>
        </div>
      </div>

      {loading && (
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      )}

      {!loading && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
            <StatCard
              label="Total Calls"
              value={calls?.total ?? 0}
              change={calls?.change_from_previous?.total}
            />
            <StatCard label="Inbound" value={calls?.inbound ?? 0} />
            <StatCard label="Outbound" value={calls?.outbound ?? 0} />
            <StatCard
              label="Completed"
              value={calls?.completed ?? 0}
              change={calls?.change_from_previous?.completed}
            />
            <StatCard label="Failed" value={calls?.failed ?? 0} />
            <StatCard
              label="Success Rate"
              value={
                calls?.success_rate != null ? `${calls.success_rate}%` : "—"
              }
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <StatCard
              label="Total Minutes"
              value={
                calls?.total_minutes != null
                  ? calls.total_minutes.toFixed(1)
                  : 0
              }
            />
            <StatCard
              label="Avg Duration"
              value={
                calls?.avg_duration_seconds != null
                  ? `${Math.round(calls.avg_duration_seconds)}s`
                  : "—"
              }
            />
            <StatCard
              label="Est. Cost (USD)"
              value={
                costs?.total_cents != null
                  ? `$${(costs.total_cents / 100).toFixed(2)}`
                  : "$0.00"
              }
            />
          </div>

          {hasLatency && (
            <>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
                <StatCard
                  label="Avg E2E Latency"
                  value={fmtMs(latency?.summary.avg_e2e_ms)}
                />
                <StatCard
                  label="P50 Latency"
                  value={fmtMs(latency?.summary.p50_e2e_ms)}
                />
                <StatCard
                  label="P95 Latency"
                  value={fmtMs(latency?.summary.p95_e2e_ms)}
                />
                {hasLatencySplit && (
                  <StatCard
                    label="Avg LLM"
                    value={fmtMs(latency?.summary.avg_llm_ms)}
                  />
                )}
                {hasLatencySplit && (
                  <StatCard
                    label="Avg TTS"
                    value={fmtMs(latency?.summary.avg_tts_ms)}
                  />
                )}
                <StatCard
                  label="Turns Measured"
                  value={latency?.summary.sample_count ?? 0}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <ChartCard title="E2E Latency Trend (daily avg)">
                  <LineChart data={latencyTrend}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => `${Math.round(v)}`}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(v) => [`${v}ms`, "Avg E2E"]}
                    />
                    <Line
                      type="monotone"
                      dataKey="avg_e2e_ms"
                      name="Avg E2E (ms)"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ChartCard>

                <ChartCard title="Latency Distribution">
                  <BarChart data={latency?.distribution ?? []}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="count" name="Turns" fill="#f59e0b" />
                  </BarChart>
                </ChartCard>
              </div>
            </>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <ChartCard title="Calls Over Time">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="total_calls"
                  name="Total Calls"
                  stroke="#6366f1"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ChartCard>

            <ChartCard title="Calls Breakdown">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar
                  dataKey="completed"
                  name="Completed"
                  stackId="a"
                  fill="#22c55e"
                />
                <Bar
                  dataKey="failed"
                  name="Failed"
                  stackId="a"
                  fill="#ef4444"
                />
              </BarChart>
            </ChartCard>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <ChartCard title="Sentiment Trend">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis domain={[-100, 100]} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line
                  type="monotone"
                  dataKey="sentiment_pct"
                  name="Sentiment"
                  stroke="#0f766e"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ChartCard>

            <ChartCard title="Conversion Funnel">
              <BarChart data={funnel}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                <XAxis dataKey="stage" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" name="Calls" fill="#2563eb" />
              </BarChart>
            </ChartCard>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top Intents</CardTitle>
            </CardHeader>
            <CardContent>
              {topIntents.length > 0 ? (
                <div className="h-60">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topIntents}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                      <XAxis dataKey="intent" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Bar dataKey="count" name="Calls" fill="#7c3aed" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No intent tags or dispositions recorded yet.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Sentiment Distribution</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <SentimentChip
                label="Positive"
                value={sentiment?.positive ?? 0}
                cls="border-emerald-200 text-emerald-700 dark:border-emerald-900 dark:text-emerald-400"
              />
              <SentimentChip
                label="Neutral"
                value={sentiment?.neutral ?? 0}
                cls="border-input text-muted-foreground"
              />
              <SentimentChip
                label="Negative"
                value={sentiment?.negative ?? 0}
                cls="border-rose-200 text-rose-700 dark:border-rose-900 dark:text-rose-400"
              />
            </CardContent>
          </Card>

          {agents?.comparison && agents.comparison.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Per-Agent Comparison</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-4 py-2.5 font-medium">Agent</th>
                        <th className="px-4 py-2.5 text-right font-medium">
                          Calls
                        </th>
                        <th className="px-4 py-2.5 text-right font-medium">
                          Completed
                        </th>
                        <th className="px-4 py-2.5 text-right font-medium">
                          Failed
                        </th>
                        <th className="px-4 py-2.5 text-right font-medium">
                          Success
                        </th>
                        <th className="px-4 py-2.5 text-right font-medium">
                          Avg Duration
                        </th>
                        <th className="px-4 py-2.5 text-right font-medium">
                          Sentiment
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {agents.comparison.map((a) => (
                        <tr key={a.agent_id}>
                          <td className="px-4 py-2.5">{a.agent_name}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums">
                            {a.total_calls}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums">
                            {a.completed}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums">
                            {a.failed}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums">
                            {a.success_rate.toFixed(1)}%
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums">
                            {Math.round(a.avg_duration_seconds)}s
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums">
                            {a.sentiment_score == null
                              ? "—"
                              : a.sentiment_score.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {agents?.top_performing && agents.top_performing.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Top Agents</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-4 py-2.5 font-medium">Agent Name</th>
                        <th className="px-4 py-2.5 text-right font-medium">
                          Calls
                        </th>
                        <th className="px-4 py-2.5 text-right font-medium">
                          Total Minutes
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {agents.top_performing.map((a) => (
                        <tr key={a.agent_id}>
                          <td className="px-4 py-2.5">{a.agent_name}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums">
                            {a.calls}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums">
                            {a.total_minutes.toFixed(1)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

const tooltipStyle: React.CSSProperties = {
  background: "var(--background)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  fontSize: 12,
};

function ChartCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactElement;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            {children}
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function SentimentChip({
  label,
  value,
  cls,
}: {
  label: string;
  value: number;
  cls: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-sm",
        cls,
      )}
    >
      <span className="font-medium">{label}:</span>
      <span className="tabular-nums">{value}</span>
    </span>
  );
}
